import React from 'react';
import { BookOpen, ListChecks, Wrench, FolderOpen, ClipboardCheck, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isCurrentWeek } from './planningApi';
import { chapterColor, termGradient } from './planningColors';

const Field = ({ icon: Icon, label, value }) => value ? (
  <div className="flex gap-2 text-sm">
    <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
    <div><span className="text-xs uppercase tracking-wide text-slate-400">{label}</span><p className="text-slate-700 whitespace-pre-line">{value}</p></div>
  </div>
) : null;

export const PlannerSchemeCards = ({ weeks, lessons, today, onGoToLessons }) => {
  const terms = [...new Set(weeks.map((w) => w.term))];
  const taughtIn = (w) => {
    const ls = lessons.filter((l) => l.term === w.term && l.week === w.week);
    return { n: ls.length, taught: ls.filter((l) => l.status === 'taught').length };
  };
  return (
    <div className="space-y-6" data-testid="planner-scheme">
      {terms.map((term) => (
        <div key={term}>
          <h3 className={`font-semibold text-white mb-3 inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${termGradient(term)} px-4 py-1.5 text-sm uppercase tracking-wider shadow-sm`}>{term}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {weeks.filter((w) => w.term === term).map((w) => {
              const now = isCurrentWeek(w, today);
              const t = taughtIn(w);
              const col = chapterColor(w.chapter);
              return (
                <div key={w.id} className={`rounded-xl border bg-white p-4 space-y-3 shadow-sm border-t-4 ${col.border} ${now ? 'ring-2 ring-emerald-400' : ''}`} data-testid={`scheme-week-${w.term.replace(/\s/g, '')}-${w.week}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-slate-400">Week {w.week} · {w.dates}{now && <Badge className="ml-2 bg-emerald-600 text-[10px]">This week</Badge>}</p>
                      <p className={`font-semibold ${col.text} inline-flex items-center gap-2`}><span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />{w.chapter}</p>
                      {w.theme && <Badge variant="outline" className="mt-1 bg-sky-50 text-sky-800 border-sky-200">{w.theme}</Badge>}
                    </div>
                    {t.n > 0 && (
                      <span className="text-xs shrink-0 text-right">
                        <span className={t.taught === t.n ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{t.taught}/{t.n} taught</span>
                        <span className="block mt-1 h-1.5 w-20 rounded-full bg-slate-100 overflow-hidden"><span className={`block h-full rounded-full bg-gradient-to-r ${col.bar}`} style={{ width: `${Math.round((100 * t.taught) / t.n)}%` }} /></span>
                      </span>
                    )}
                  </div>
                  <Field icon={ListChecks} label="Lesson topics" value={w.topics} />
                  <Field icon={BookOpen} label="Learning focus (textbook sections)" value={w.focus} />
                  <Field icon={Wrench} label="Key activities" value={w.activities} />
                  <Field icon={FolderOpen} label="Resources" value={w.resources} />
                  <Field icon={ClipboardCheck} label="Assessment" value={w.sow_assessment || w.assessment} />
                  {w.extra && Object.entries(w.extra).map(([k, v]) => <Field key={k} icon={ListChecks} label={k} value={v} />)}
                  <Button size="sm" variant="outline" className="w-full" onClick={() => onGoToLessons(w)} data-testid={`scheme-open-lessons-${w.term.replace(/\s/g, '')}-${w.week}`}>
                    Open this week's lessons <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
